import { z } from 'zod';

import {
  FREE_TRIAL_BOOKING_WINDOW_LABEL,
  FREE_TRIAL_EARLIEST_END_TIME,
  FREE_TRIAL_END_TIME,
  FREE_TRIAL_LATEST_START_TIME,
  FREE_TRIAL_START_TIME,
  FREE_TRIAL_TIME_WINDOW_LABEL,
  isFreeTrialBookingDate,
  isFreeTrialStep,
} from '@mds/shared/lib/free-trial';
import { minutesOf } from '@mds/shared/lib/dates';
import { clockLabel } from '@mds/shared/lib/format';
import { accepted, email, phone, picked, required, tooLong } from '@mds/shared/lib/validation';
import type { BookingPaymentMethod } from '@mds/shared/types/booking';

/** The ways a visitor can pay for a desk: a subset of the API's booking methods. */
export const DESK_PAYMENT_METHODS =['cash', 'whish'] as const satisfies readonly BookingPaymentMethod[];
export type DeskPaymentMethod = (typeof DESK_PAYMENT_METHODS)[number];

/** Spelled as the admin app spells them, so both ends of the chat agree. */
export const PAYMENT_METHOD_LABELS: Record<DeskPaymentMethod, string> = {
  cash: 'Cash',
  whish: 'Whish',
};

/**
 * Where a trial visitor lives: the areas the opening week is aimed at, A to Z
 * (the dropdown lists them in this order), and `other` last for anyone from
 * further away.
 */
const REGIONS = ['akkar', 'danniyeh', 'minieh', 'tripoli', 'other'] as const;
export type Region = (typeof REGIONS)[number];
export const REGION_LABELS: Record<Region, string> = {
  akkar: 'Akkar',
  danniyeh: 'Danniyeh',
  minieh: 'Minieh',
  tripoli: 'Tripoli',
  other: 'Other',
};

const OCCUPATIONS = ['student', 'employee'] as const;
export type Occupation = (typeof OCCUPATIONS)[number];
export const OCCUPATION_LABELS: Record<Occupation, string> = {
  student: 'Student',
  employee: 'Employee',
};

/** LinkedIn's workplace types, in its words, so they read as familiar. */
const WORK_MODES = ['onsite', 'remote', 'hybrid'] as const;
export type WorkMode = (typeof WORK_MODES)[number];
export const WORK_MODE_LABELS: Record<WorkMode, string> = {
  onsite: 'On-site',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

/** An `hh:mm` time on the free trial's step (`FREE_TRIAL_STEP_MINUTES`), as the API checks it. */
export const isTrialTime = (value: string) =>
  /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) && isFreeTrialStep(minutesOf(value));

/** One of `values`, or `''` while nothing is picked; whether that is allowed is the refinement's call. */
const choice = <const T extends readonly [string, ...string[]]>(values: T) =>
  z.union([z.literal(''), z.enum(values)]);

/**
 * A daily desk booking, checked before it is written out for WhatsApp. Nothing
 * is sent to the API, so there is no request type to mirror: the limits match
 * the contact form's.
 *
 * Two forms in one shape. A paid booking (`trial` false) asks the visitor to choose a package and
 * how to pay. The opening week's free trial (`trial` true) uses its system package, asks for a
 * start/end time, and asks where the visitor lives and whether they study or work: a student names
 * their major, an employee how and in what field they work. The fields the other mode uses stay in
 * the shape, empty, so the page has one form type.
 *
 * The desk comes from the floor map and the package from the list, so both
 * are ids where empty means nothing picked yet.
 */
export const deskBookingSchema = (trial: boolean, today: string) =>
  z
    .object({
      fullName: required('Full name').max(160, tooLong(160)),
      email,
      phone,
      bookingDate: required(trial ? 'Date' : 'Start date')
        .refine(
          (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= today,
          'Pick a date from today on.',
        )
        .refine(
          (value) => !trial || isFreeTrialBookingDate(value),
          `Free trials are only available ${FREE_TRIAL_BOOKING_WINDOW_LABEL}.`,
        ),
      startTime: z
        .string()
        .refine(
          (value) => value === '' || isTrialTime(value),
          'Pick a start time on the hour.',
        ),
      endTime: z
        .string()
        .refine(
          (value) => value === '' || isTrialTime(value),
          'Pick an end time on the hour.',
        ),
      desk: picked('Pick a desk on the map.'),
      packageId: z.string(),
      paymentMethod: choice(DESK_PAYMENT_METHODS),
      region: choice(REGIONS),
      occupation: choice(OCCUPATIONS),
      major: z.string().trim().max(120, tooLong(120)),
      workMode: choice(WORK_MODES),
      workField: z.string().trim().max(120, tooLong(120)),
      policyAccepted: accepted('Please accept the booking and use terms to continue.'),
    })
    .superRefine(
      (values, ctx) => {
        const missing = (path: keyof typeof values, message: string) =>
          ctx.addIssue({ code: 'custom', path: [path], message });

        if (!trial) {
          if (!values.packageId) missing('packageId', 'Choose a package.');
          if (!values.paymentMethod) missing('paymentMethod', 'Choose how you will pay.');
          return;
        }

        if (!values.startTime) missing('startTime', 'Pick a start time.');
        if (!values.endTime) missing('endTime', 'Pick an end time.');
        if (
          values.startTime &&
          (values.startTime < FREE_TRIAL_START_TIME ||
            values.startTime > FREE_TRIAL_LATEST_START_TIME)
        ) {
          missing('startTime', `Choose a start time within ${FREE_TRIAL_TIME_WINDOW_LABEL}.`);
        }
        if (
          values.endTime &&
          (values.endTime < FREE_TRIAL_EARLIEST_END_TIME || values.endTime > FREE_TRIAL_END_TIME)
        ) {
          missing('endTime', `Choose an end time within ${FREE_TRIAL_TIME_WINDOW_LABEL}.`);
        }
        // Compared as minutes since midnight, so 9:00 PM (1260) is after
        // 9:00 AM (540) and AM and PM can never be read the wrong way round.
        if (values.startTime && values.endTime) {
          const start = minutesOf(values.startTime);
          const end = minutesOf(values.endTime);
          if (end <= start) {
            missing('endTime', `End time must be after the start time (${clockLabel(start)}).`);
          }
        }
        if (!values.region) missing('region', 'Choose where you live.');
        if (!values.occupation) missing('occupation', 'Tell us if you study or work.');
        if (values.occupation === 'student' && !values.major?.trim()) {
          missing('major', 'Major is required.');
        }
        if (values.occupation === 'employee') {
          if (!values.workMode) missing('workMode', 'Choose your workplace type.');
          if (!values.workField?.trim()) missing('workField', 'Field of work is required.');
        }
      },
      // Run even while another field is still wrong, so every gap shows on the
      // first send rather than these only once the name and email pass.
      { when: () => true },
    );

export type DeskBookingForm = z.input<ReturnType<typeof deskBookingSchema>>;
export type DeskBooking = z.output<ReturnType<typeof deskBookingSchema>>;
